/**
 * StudioPlacementIndicator — the layout's placement state, and the two bulk
 * actions that move along that axis.
 *
 * The Test-Fit Studio has no "packing mode" flag: the packer auto-arranges every
 * component, and pinning fixes individual pieces in place (dashed outline, the
 * packer flows the rest around them). The two ends of that axis ARE expressible,
 * though — "auto" is nothing pinned, "manual" is everything pinned where it sits
 * — so this control both REPORTS the hybrid state and offers the bulk moves:
 *
 *   Unpin all → hand the whole layout back to the packer  (auto)
 *   Pin all   → freeze every component where it sits      (manual)
 *
 * It is presented as an explicit two-state MODE SWITCH (Auto | Manual) rather
 * than a menu of verbs: users look for "how do I turn auto-pack off", so the
 * control names the modes and does the bulk pin/unpin behind them. A partly
 * pinned layout is a genuine third state and is reported as "N of M pinned"
 * instead of being forced into one of the two. With no action handlers
 * (read-only scenarios) it degrades to a passive readout.
 */
import React from 'react';
import { LayoutGrid, Pin } from 'lucide-react';

export interface StudioPlacementIndicatorProps {
  /** Components fixed in place by a manual pin (dashed on the canvas). */
  pinnedCount: number;
  /** Components the packer arranged automatically (solid on the canvas). */
  autoCount: number;
  /** Clear every pin — the whole layout returns to the packer. */
  onUnpinAll?: () => void;
  /** Pin every packer-placed component at its current position. */
  onPinAll?: () => void;
  /** Hides the bulk actions while the scenario cannot be mutated. */
  readOnly?: boolean;
}

export const StudioPlacementIndicator: React.FC<StudioPlacementIndicatorProps> = ({
  pinnedCount,
  autoCount,
  onUnpinAll,
  onPinAll,
  readOnly = false,
}) => {
  const total = pinnedCount + autoCount;
  const hasPins = pinnedCount > 0;
  const pinnedPct = total > 0 ? Math.round((pinnedCount / total) * 100) : 0;
  const actionable = !readOnly && (onUnpinAll != null || onPinAll != null);

  const stateLabel = hasPins
    ? `Placement: ${pinnedCount} pinned, ${autoCount} auto-packed`
    : 'Placement: auto-packed';
  const stateHint = hasPins
    ? `${pinnedCount} component${pinnedCount === 1 ? '' : 's'} pinned (dashed outline) — the packer arranges the other ${autoCount}. Drag to reposition, or unpin to hand it back to the packer.`
    : 'Auto-packed — the packer arranges every component. Drag or pin one to place it by hand; its outline turns dashed and the packer flows the rest around it.';

  const display = (
    <>
      {hasPins
        ? <Pin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
        : <LayoutGrid className="h-3.5 w-3.5 shrink-0 text-text-muted" aria-hidden />}
      <span className="flex flex-col justify-center leading-none">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted/70">Placement</span>
        <span className="mt-[3px] whitespace-nowrap text-[10px] font-bold uppercase tracking-wide tabular-nums">
          {hasPins ? (
            <>
              <span className="text-primary">{pinnedCount} pinned</span>
              <span className="text-text-muted"> · {autoCount} auto</span>
            </>
          ) : (
            <span className="text-text-secondary">Auto-packed</span>
          )}
        </span>
      </span>
      {hasPins && (
        // Fill gauge: the pinned share of the fleet, bottom-anchored like a level meter.
        <span className="ml-0.5 flex h-5 w-1 flex-col justify-end overflow-hidden rounded-sm border border-border/60 bg-background/50" aria-hidden>
          <span className="w-full bg-primary" style={{ height: `${pinnedPct}%` }} />
        </span>
      )}
    </>
  );

  const shellClass = `flex min-h-8 shrink-0 items-center gap-1.5 border px-2 shadow-[inset_0_1px_2px_rgba(0,0,0,0.28)] transition-colors max-[599px]:min-h-11 ${
    hasPins ? 'border-primary/45 bg-primary/[0.07]' : 'border-border/80 bg-background/60'
  }`;

  if (!actionable) {
    return (
      <div
        role="status"
        data-testid="studio-placement-indicator"
        data-placement-mode={hasPins ? 'manual' : 'auto'}
        aria-label={stateLabel}
        title={stateHint}
        className={shellClass}
      >
        {display}
      </div>
    );
  }

  // Explicit two-state mode switch. Users reach for "how do I turn auto-pack
  // off?", so the control names the MODES (Auto / Manual) rather than the two
  // verbs behind them — Auto = nothing pinned, Manual = everything pinned where
  // it sits. A partly-pinned layout is a real third state, reported under the
  // switch rather than pretended away.
  const mixed = hasPins && autoCount > 0;
  const modeButton = (
    mode: 'auto' | 'manual',
    label: string,
    onClick: (() => void) | undefined,
    disabled: boolean,
    title: string,
  ): React.JSX.Element => {
    const selected = mode === 'auto' ? !hasPins : hasPins && autoCount === 0;
    return (
      <button
        type="button"
        aria-pressed={selected}
        disabled={disabled || onClick == null}
        onClick={onClick}
        title={title}
        className={`min-h-6 px-2 text-[10px] font-bold uppercase tracking-wide transition-colors focus-visible:relative focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40 ${
          selected ? 'bg-primary text-on-primary' : 'text-text-secondary hover:bg-background hover:text-text-primary'
        }`}
        data-testid={mode === 'auto' ? 'studio-placement-unpin-all' : 'studio-placement-pin-all'}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      data-testid="studio-placement-indicator"
      data-placement-mode={hasPins ? 'manual' : 'auto'}
      className={`flex min-h-8 shrink-0 items-center gap-2 rounded-md border px-2 shadow-[inset_0_1px_2px_rgba(0,0,0,0.28)] max-[599px]:min-h-11 ${
        hasPins ? 'border-primary/45 bg-primary/[0.07]' : 'border-border/80 bg-background/60'
      }`}
    >
      <span role="status" className="sr-only">{stateLabel}</span>
      <span className="flex flex-col justify-center leading-none">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted/70">Placement</span>
        {mixed && (
          <span className="mt-[3px] whitespace-nowrap text-[10px] font-bold uppercase tracking-wide tabular-nums text-primary">
            {pinnedCount} of {total} pinned
          </span>
        )}
      </span>
      <span
        className="inline-flex overflow-hidden rounded-md border border-border"
        role="group"
        aria-label="Placement mode"
      >
        {modeButton(
          'auto',
          'Auto',
          onUnpinAll,
          !hasPins,
          hasPins
            ? `Unpin all ${pinnedCount} — hand the whole layout back to the packer`
            : 'Auto-packed — the packer arranges every component',
        )}
        {modeButton(
          'manual',
          'Manual',
          onPinAll,
          autoCount === 0,
          autoCount > 0
            ? `Pin all ${autoCount} where they sit — the packer stops rearranging them`
            : 'Every component is already pinned',
        )}
      </span>
    </div>
  );
};
